import DownloadIcon from "@/assets/icons/download.svg";
import { threadStore } from "@/store/thread";
import { formatDuration } from "@/util/datetime";
import { downloadFile } from "@/util/file";
import { useWavesurfer } from "@wavesurfer/react";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState } from "react";
import RegionsPlugin from "wavesurfer.js/dist/plugins/regions.esm.js";
import Button from "../Button";
import styles from "./index.module.css";
import PauseIcon from "./pause.svg";
import PlayIcon from "./play.svg";

interface IProps {
  title?: string;
  description?: string;
  audioUrl: string;
  downloadFilename?: string;
  analysisData?: {
    beats?: Array<{
      time: number;
      loudness: number;
    }>;
    beat_positions?: number[];
    downbeats?: number[];
    phrases?: Array<{
      start: number;
      end: number;
      label: string;
    }>;
  };
  stopSignal?: number;
}
const PHRASE_COLORS = [
  [59, 130, 246], // Blue
  [16, 185, 129], // Emerald
  [245, 158, 11], // Amber
  [239, 68, 68], // Red
  [139, 92, 246], // Violet
  [236, 72, 153], // Pink
  [6, 182, 212], // Cyan
  [249, 115, 22], // Orange
];

const LOUDNESS_CONFIG = {
  WIDTH: 60,
  HEIGHT: 20,
  BAR_WIDTH: 4,
  BAR_GAP: 2,
  get BAR_STEP() {
    return this.BAR_WIDTH + this.BAR_GAP;
  },
  get CENTER_X() {
    return this.WIDTH / 2;
  },
  get BAR_OFFSET() {
    return this.BAR_WIDTH / 2;
  },
  SLICE_BUFFER: 8,
};

export const AudioWavePlayer = observer((props: IProps) => {
  const { title, description, audioUrl, downloadFilename, analysisData } =
    props;
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoomLevel, setZoomLevel] = useState(analysisData ? 100 : 0);
  const thread = threadStore.thread;

  const plugins = useMemo(() => [RegionsPlugin.create()], []);

  const waveHeight = useMemo(() => (analysisData ? 28 : 20), [analysisData]);

  const { wavesurfer, isPlaying, isReady, currentTime } = useWavesurfer({
    container: containerRef,
    height: waveHeight,
    barWidth: 1.3,
    barHeight: 2,
    barGap: 1.7,
    barRadius: 2,
    waveColor: "#3b3d41",
    progressColor: "#9aa2b1",
    url: audioUrl,
    minPxPerSec: 100, // Stable value to prevent re-initialization
    autoCenter: true,
    autoScroll: true,
    hideScrollbar: true,
    plugins,
  });

  const duration = wavesurfer?.getDuration() || 0;

  // Render native markers (regions)
  useEffect(() => {
    if (!wavesurfer || !isReady) return;

    const regions = wavesurfer
      .getActivePlugins()
      .find((p) => p instanceof RegionsPlugin) as RegionsPlugin;
    if (!regions) return;

    regions.clearRegions();

    // Phrases as background regions
    analysisData?.phrases?.forEach((phrase, idx) => {
      regions.addRegion({
        start: phrase.start,
        end: phrase.end,
        color: `rgba(${PHRASE_COLORS[idx % PHRASE_COLORS.length]}, 0.05)`, // Very light background
        drag: false,
        resize: false,
      });
      // Add a thin line at the top
      regions.addRegion({
        start: phrase.start,
        end: phrase.end,
        color: `rgba(${PHRASE_COLORS[idx % PHRASE_COLORS.length]}, 0.05)`,
        drag: false,
        resize: false,
      });
    });

    // Beats as markers (start === end)
    analysisData?.beats?.forEach((beat, idx) => {
      const isDownbeat = analysisData.beat_positions?.[idx] === 1;
      regions.addRegion({
        start: beat.time,
        end: beat.time,
        color: isDownbeat ? "#3B82F6" : "#9aa2b140", // Blue for downbeat, grey for beat
        drag: false,
        resize: false,
        // Wavesurfer markers are vertical lines by default if start === end
      });
    });
  }, [wavesurfer, isReady, analysisData]);

  // Sync zoom level without re-initializing wavesurfer instance
  useEffect(() => {
    if (wavesurfer && isReady) {
      wavesurfer.zoom(zoomLevel);
    }
  }, [wavesurfer, isReady, zoomLevel]);

  // Handle stop signal from AssetsExplorerStore
  useEffect(() => {
    if (props.stopSignal && wavesurfer) {
      wavesurfer.pause();
      isPlayingRef.current = false;
    }
  }, [props.stopSignal, wavesurfer]);

  const isPlayingRef = useRef(false);

  const handlePlay = () => {
    if (!isPlayingRef.current) {
      isPlayingRef.current = true;
      wavesurfer?.play();
    }
  };

  const handlePlayPause = () => {
    if (isPlayingRef.current) {
      wavesurfer?.pause();
    } else {
      wavesurfer?.play();
    }
    isPlayingRef.current = !isPlayingRef.current;
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (downloadFilename) {
      downloadFile(audioUrl, downloadFilename);
      return;
    }
    const soundName = (title || "Sound").replace(/\s+/g, "_");
    downloadFile(audioUrl, `${soundName}.mp3`);
  };

  const seekTo = (time: number) => {
    wavesurfer?.setTime(time);
    if (!isPlayingRef.current) {
      wavesurfer?.play();
      isPlayingRef.current = true;
    }
  };

  // Pre-calculate loudness range for normalization
  const loudnessRange = useMemo(() => {
    if (!analysisData?.beats?.length) return { min: -60, max: 0 };
    const values = analysisData.beats.map((b) => b.loudness);
    return {
      min: Math.min(...values),
      max: Math.max(...values),
    };
  }, [analysisData]);

  // Find current beat index and interpolation factor
  const currentBeatIndex = useMemo(() => {
    if (!analysisData?.beats?.length) return -1;
    const idx = analysisData.beats.findIndex((b, i) => {
      const nextBeat = analysisData.beats![i + 1];
      return (
        currentTime >= b.time && (!nextBeat || currentTime < nextBeat.time)
      );
    });
    return idx === -1 ? 0 : idx;
  }, [analysisData, currentTime]);

  const interpolationProgress = useMemo(() => {
    if (currentBeatIndex === -1 || !analysisData?.beats) return 0;
    const currentBeat = analysisData.beats[currentBeatIndex];
    const nextBeat = analysisData.beats[currentBeatIndex + 1];
    if (!nextBeat) return 0;
    return (
      (currentTime - currentBeat.time) / (nextBeat.time - currentBeat.time)
    );
  }, [analysisData, currentTime, currentBeatIndex]);

  const visibleBeatsData = useMemo(() => {
    if (currentTime === 0) return { beats: [], startIdx: 0 };
    if (!analysisData?.beats) return { beats: [], startIdx: 0 };
    const start = Math.max(0, currentBeatIndex - LOUDNESS_CONFIG.SLICE_BUFFER);
    const end = Math.min(
      analysisData.beats.length,
      currentBeatIndex + LOUDNESS_CONFIG.SLICE_BUFFER + 1,
    );
    const beats = analysisData.beats.slice(start, end).map((beat, idx) => {
      const globalIdx = start + idx;
      const rawLoudness = beat.loudness;
      const normalizedLoudness =
        loudnessRange.max > loudnessRange.min
          ? (rawLoudness - loudnessRange.min) /
            (loudnessRange.max - loudnessRange.min)
          : 0.5;
      const intensity = Math.pow(normalizedLoudness, 0.6);
      return {
        id: `beat-${globalIdx}`,
        height: Math.min(100, Math.max(10, intensity * 100)),
        isCurrent: globalIdx === currentBeatIndex,
      };
    });
    return { beats, startIdx: start };
  }, [analysisData, currentBeatIndex, currentTime, loudnessRange]);

  const currentPhraseIndex = analysisData?.phrases?.findIndex(
    (p) => currentTime >= p.start && currentTime <= p.end,
  );

  return (
    <div
      className={classNames(
        "relative flex flex-col items-start gap-4 p-4 flex-shrink-0 rounded-xl bg-background-card border border-text-primary/[.05] overflow-hidden",
      )}
    >
      <div className="flex items-center gap-4">
        {title && (
          <div className="text-text-primary text-base font-bold leading-normal">
            {title}
          </div>
        )}
      </div>

      {isReady && thread?.owner && (
        <Button
          type="plain"
          className="absolute top-4 right-4 p-0 h-auto!"
          onClick={handleDownload}
        >
          <DownloadIcon className="w-4 h-4 text-[#98A2B3]" />
        </Button>
      )}
      {audioUrl && (
        <div className="w-full flex flex-col gap-5">
          {/* Waveform Section */}
          <div className="flex items-center gap-4">
            {isReady ? (
              <>
                {!isPlaying ? (
                  <PlayIcon
                    className="cursor-pointer text-primary"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handlePlayPause();
                    }}
                  />
                ) : (
                  <PauseIcon
                    className="cursor-pointer text-primary"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handlePlayPause();
                    }}
                  />
                )}
              </>
            ) : (
              <PlayIcon className="text-gray-secondary opacity-30" />
            )}

            <div className="flex-1 relative group overflow-hidden">
              <div
                ref={containerRef}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  if (isReady) handlePlay();
                }}
                className={`w-full h-full relative z-10 ${
                  isReady ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
              />
              {!isReady && (
                <div
                  className={`absolute w-full opacity-50 rounded-sm inset-0 ${styles.skeletonWave}`}
                  style={{ height: `${waveHeight}px` }}
                />
              )}
            </div>

            <div className="text-[12px] font-mono text-text-tertiary w-[100px] text-right">
              {isReady && (
                <>
                  {formatDuration(currentTime)} / {formatDuration(duration)}
                </>
              )}
            </div>
          </div>

          {/* New Modules Row */}
          {analysisData && (
            <div className="w-full flex justify-between gap-10 px-1">
              {/* Phrase Navigation */}
              <div className="flex flex-col flex-3 shrink-0 gap-2 justify-between">
                <div className="text-[11px] uppercase tracking-wider text-text-tertiary font-bold">
                  Phrases
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-2 items-center">
                  {analysisData?.phrases?.map((phrase, idx) => {
                    const color = `rgb(${
                      PHRASE_COLORS[idx % PHRASE_COLORS.length]
                    })`;
                    const isActive = currentPhraseIndex === idx;
                    return (
                      <div
                        key={`phrase-item-${idx}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          seekTo(phrase.start);
                        }}
                        className={classNames(
                          "group flex items-center gap-1.5 cursor-pointer transition-all",
                          isActive
                            ? "opacity-100"
                            : "opacity-60 hover:opacity-100",
                        )}
                      >
                        <div
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: color }}
                        />
                        <span
                          className={classNames(
                            "text-[12px] font-medium transition-colors",
                            isActive
                              ? "text-text-primary"
                              : "text-text-secondary",
                          )}
                        >
                          {phrase.label || `P${idx + 1}`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-10">
                {/* Spectrum Visualizer */}
                <div className="flex flex-col gap-2 items-center">
                  <div className="text-[11px] uppercase tracking-wider text-text-tertiary font-bold">
                    LOUDNESS
                  </div>
                  <div
                    className="relative overflow-hidden shrink-0"
                    style={{
                      width: `${LOUDNESS_CONFIG.WIDTH}px`,
                      height: `${LOUDNESS_CONFIG.HEIGHT}px`,
                    }}
                  >
                    <div
                      className="absolute h-full flex items-end"
                      style={{
                        gap: `${LOUDNESS_CONFIG.BAR_GAP}px`,
                        transform: `translateX(${
                          LOUDNESS_CONFIG.CENTER_X -
                          ((currentBeatIndex -
                            visibleBeatsData.startIdx +
                            interpolationProgress) *
                            LOUDNESS_CONFIG.BAR_STEP +
                            LOUDNESS_CONFIG.BAR_OFFSET)
                        }px)`,
                      }}
                    >
                      {visibleBeatsData.beats.map((beat) => (
                        <div
                          key={beat.id}
                          className={classNames(
                            styles.spectrumBar,
                            beat.isCurrent && styles.activeBar,
                          )}
                          style={{
                            height: `${beat.height}%`,
                            width: `${LOUDNESS_CONFIG.BAR_WIDTH}px`,
                            flexShrink: 0,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Zoom Control */}
                <div className="flex flex-col gap-2 justify-between">
                  <div className="text-[11px] uppercase tracking-wider text-text-tertiary font-bold">
                    Zoom
                  </div>
                  <div className="flex flex-1 items-center gap-2 w-20">
                    <input
                      type="range"
                      min="0"
                      max="500"
                      value={zoomLevel}
                      onChange={(e) => {
                        e.stopPropagation();
                        setZoomLevel(Number(e.target.value));
                      }}
                      className={styles.zoomSlider}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {description && (
        <div className="text-text-tertiary text-[12px] mt-2 opacity-60">
          Prompt: {description}
        </div>
      )}
    </div>
  );
});
